import * as Sentry from '@sentry/browser'

import { EmbeddedCredentialMode } from '@web-apps/utils-shared'

import { configureSentryErrors } from './errors'
import { getUserAuthSession } from './flows'
import { SessionType, UserRoles } from './types'

export const SEAMLESS_AUTH_STORAGE_KEY = 'isSeamlessAuth'
const SEAMLESS_AUTH_QUERY_PARAM = 'from-metapic'

export const captureSeamlessAuthMarker = (): void => {
  if (typeof window === 'undefined') return

  if (
    new URLSearchParams(window.location.search).has(SEAMLESS_AUTH_QUERY_PARAM)
  )
    localStorage.setItem(SEAMLESS_AUTH_STORAGE_KEY, 'true')
}

export const hasSeamlessAuthMarker = (): boolean => {
  if (typeof window === 'undefined') return false

  return (
    new URLSearchParams(window.location.search).has(
      SEAMLESS_AUTH_QUERY_PARAM
    ) || localStorage.getItem(SEAMLESS_AUTH_STORAGE_KEY) === 'true'
  )
}

export type ZezamWhoamiResponse = { user_id: string }

export type SessionStatus =
  | 'authenticated'
  | 'anonymous'
  | 'no-zezam-account'
  | 'needs-native-auth'
  | 'impersonation-failed'

export type ResolvedSession = {
  status: SessionStatus
  session: SessionType | null
}

type ResolveSessionParams = {
  isEmbedded: boolean
  hasEmbeddedCredential: boolean
  embeddedCredentialMode?: EmbeddedCredentialMode | null
  isSeamlessAuth?: boolean
  impersonatedUserId?: string | null
  fetchZezamSession: () => Promise<ZezamWhoamiResponse>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isCredentialRejected = (error: any): boolean => {
  const status = error?.response?.status ?? error?.status

  return status === 401 || status === 403
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reportSessionError = (action: string, error: any): void => {
  Sentry.withScope((scope) => {
    scope
      .setTag('operations', 'ory')
      .setLevel('error')
      .setFingerprint([action])
      .setExtras({ action, ...configureSentryErrors(error) })
    Sentry.captureException(error)
  })
}

const buildWhoamiSession = (zezamUserId: string): SessionType => ({
  active: true,
  source: 'whoami',
  identity: {
    metadata_public: {
      zezam_user_id: zezamUserId,
      roles: [],
    },
  },
})

const getOrySession = async (): Promise<SessionType | null> => {
  const session = await getUserAuthSession()

  return session
    ? { ...(session as unknown as SessionType), source: 'ory' }
    : null
}

const fromOrySession = (session: SessionType): ResolvedSession => ({
  status: session.identity?.metadata_public?.zezam_user_id
    ? 'authenticated'
    : 'no-zezam-account',
  session,
})

const resolveWebSession = async (): Promise<ResolvedSession> => {
  try {
    const session = await getOrySession()

    if (!session) return { status: 'anonymous', session: null }

    return fromOrySession(session)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (!isCredentialRejected(error))
      reportSessionError('getUserAuthSession', error)

    return { status: 'anonymous', session: null }
  }
}

const resolveSeamlessSession = async ({
  fetchZezamSession,
}: {
  fetchZezamSession: () => Promise<ZezamWhoamiResponse>
}): Promise<ResolvedSession> => {
  try {
    const { user_id: userId } = await fetchZezamSession()

    if (!userId) return { status: 'anonymous', session: null }

    return { status: 'authenticated', session: buildWhoamiSession(userId) }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (!isCredentialRejected(error))
      reportSessionError('seamlessWhoami', error)

    return { status: 'anonymous', session: null }
  }
}

const resolveEmbeddedCookieSession =
  async (): Promise<ResolvedSession | null> => {
    try {
      const session = await getOrySession()

      return session ? fromOrySession(session) : null
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (!isCredentialRejected(error))
        reportSessionError('embeddedOrySession', error)

      return null
    }
  }

const resolveEmbeddedSession = async ({
  hasEmbeddedCredential,
  embeddedCredentialMode,
  fetchZezamSession,
}: Pick<
  ResolveSessionParams,
  'hasEmbeddedCredential' | 'embeddedCredentialMode' | 'fetchZezamSession'
>): Promise<ResolvedSession> => {
  if (!hasEmbeddedCredential)
    return { status: 'needs-native-auth', session: null }

  if (embeddedCredentialMode === 'cookie') {
    const orySession = await resolveEmbeddedCookieSession()

    if (orySession) return orySession
  }

  try {
    const { user_id: userId } = await fetchZezamSession()

    if (!userId) return { status: 'no-zezam-account', session: null }

    return { status: 'authenticated', session: buildWhoamiSession(userId) }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (!isCredentialRejected(error)) {
      reportSessionError('embeddedWhoami', error)

      return { status: 'needs-native-auth', session: null }
    }

    return { status: 'no-zezam-account', session: null }
  }
}

const getImpersonatingAdminRoles = async (): Promise<UserRoles[]> => {
  try {
    const adminSession = await getUserAuthSession()

    return (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((adminSession?.identity?.metadata_public as any)
        ?.roles as UserRoles[]) ?? []
    )
  } catch {
    return []
  }
}

const resolveImpersonatedSession = async ({
  fetchZezamSession,
}: {
  fetchZezamSession: () => Promise<ZezamWhoamiResponse>
}): Promise<ResolvedSession> => {
  try {
    const { user_id: userId } = await fetchZezamSession()

    if (!userId) return { status: 'impersonation-failed', session: null }

    const roles = await getImpersonatingAdminRoles()
    const session = buildWhoamiSession(userId)

    return {
      status: 'authenticated',
      session: {
        ...session,
        identity: {
          ...session.identity,
          metadata_public: { ...session.identity.metadata_public, roles },
        },
      },
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (!isCredentialRejected(error))
      reportSessionError('impersonatedWhoami', error)

    return { status: 'impersonation-failed', session: null }
  }
}

export const resolveSession = async ({
  isEmbedded,
  hasEmbeddedCredential,
  embeddedCredentialMode = null,
  isSeamlessAuth = false,
  impersonatedUserId,
  fetchZezamSession,
}: ResolveSessionParams): Promise<ResolvedSession> => {
  if (impersonatedUserId)
    return resolveImpersonatedSession({ fetchZezamSession })

  if (isEmbedded)
    return resolveEmbeddedSession({
      hasEmbeddedCredential,
      embeddedCredentialMode,
      fetchZezamSession,
    })

  if (isSeamlessAuth) return resolveSeamlessSession({ fetchZezamSession })

  return resolveWebSession()
}

export const isMetapicUserKnown = (session: SessionType | null): boolean =>
  session?.source === 'ory'

export const getKnownMetapicUserId = (
  session: SessionType | null
): number | undefined =>
  session?.source === 'ory'
    ? session.identity?.metadata_public?.metapic_user_id
    : undefined
